/**
 * PID 参数 Store（Zustand）
 * 管理四环 PID 参数、修改历史和下发确认状态
 */
import { create } from 'zustand';
import type { PidLoop, PidParameters, PidHistoryEntry } from '@fluxlab/protocols';
import { DEFAULT_PID_PARAMS, PID_LOOPS } from '@fluxlab/protocols';

/** PID 参数 Store 接口 */
interface PidParamsStore {
  /** Current PID params per loop */
  params: Record<PidLoop, PidParameters>;
  /** PID history per loop */
  history: Record<PidLoop, PidHistoryEntry[]>;
  /** Currently selected loop for editing */
  selectedLoop: PidLoop;
  /** Whether a PID write is in flight */
  pending: boolean;
  /** Last ack message */
  lastAck: { loop: PidLoop; success: boolean; message?: string } | null;

  setSelectedLoop: (loop: PidLoop) => void;
  setParams: (loop: PidLoop, params: PidParameters) => void;
  setHistory: (loop: PidLoop, history: PidHistoryEntry[]) => void;
  setPending: (pending: boolean) => void;
  setLastAck: (ack: { loop: PidLoop; success: boolean; message?: string } | null) => void;
  resetToDefaults: (loop: PidLoop) => void;
}

/** PID 参数 Store（Zustand）：管理四环 PID 参数、修改历史与下发确认状态 */
export const usePidParamsStore = create<PidParamsStore>((set) => ({
  params: { ...DEFAULT_PID_PARAMS },
  history: { id: [], iq: [], speed: [], position: [] } as Record<PidLoop, PidHistoryEntry[]>,
  selectedLoop: 'iq',
  pending: false,
  lastAck: null,

  setSelectedLoop: (loop) => set({ selectedLoop: loop }),
  setParams: (loop, params) =>
    set((state) => ({
      params: { ...state.params, [loop]: params },
    })),
  setHistory: (loop, history) =>
    set((state) => ({
      history: { ...state.history, [loop]: history },
    })),
  setPending: (pending) => set({ pending }),
  setLastAck: (lastAck) => set({ lastAck, pending: false }),
  resetToDefaults: (loop) =>
    set((state) => ({
      params: { ...state.params, [loop]: { ...DEFAULT_PID_PARAMS[loop] } },
    })),
}));
